'use client';

import { useState } from 'react';
import { Check, X, ShieldCheck } from 'lucide-react';

const features = [
  { name: 'Claim verification & provenance', free: true, pro: true, premium: true, executive: true },
  { name: 'Tailored resume generation', free: '3/month', pro: '25/month', premium: '100/month', executive: 'Unlimited' },
  { name: 'Cover letter generation', free: '1/month', pro: '25/month', premium: '100/month', executive: 'Unlimited' },
  { name: 'ATS match scoring', free: 'Basic', pro: 'Advanced', premium: 'Advanced', executive: 'Advanced' },
  { name: 'Keyword gap analysis', free: false, pro: true, premium: true, executive: true },
  { name: 'Upload career documents', free: '5 docs', pro: '25 docs', premium: '100 docs', executive: 'Unlimited' },
  { name: 'AI career interview', free: '1 session', pro: '5/month', premium: 'Unlimited', executive: 'Unlimited' },
  { name: 'AI mock interview (voice)', free: '1/month', pro: '5/month', premium: '20/month', executive: 'Unlimited' },
  { name: 'AI chat for revisions', free: '10 msgs/month', pro: '100 msgs/month', premium: '500/month', executive: 'Unlimited' },
  { name: 'Email templates', free: 'Basic', pro: 'AI personalization', premium: 'AI personalization', executive: 'AI personalization' },
  { name: 'Job application tracking', free: '10 jobs', pro: 'Unlimited', premium: 'Unlimited', executive: 'Unlimited' },
  { name: 'AI headshot enhancement', free: false, pro: '5/month', premium: '20/month', executive: 'Unlimited' },
  { name: 'LinkedIn optimization', free: false, pro: true, premium: true, executive: true },
  { name: 'PDF resume templates', free: '1 template', pro: 'All templates', premium: 'All templates', executive: 'All templates' },
  { name: 'Priority support', free: false, pro: true, premium: true, executive: true },
  { name: 'Executive Suite (Value Profiles, executive documents, role playbooks, AI role research)', free: false, pro: false, premium: false, executive: true },
];

function FeatureValue({ value }: { value: boolean | string }) {
  if (value === true) return <Check className="w-5 h-5 text-green-600 mx-auto" />;
  if (value === false) return <X className="w-5 h-5 text-gray-300 mx-auto" />;
  return <span className="text-gray-900 font-medium">{value}</span>;
}

export default function PricingContent() {
  const [annual, setAnnual] = useState(true);

  const proMonthly = 19;
  const proAnnualMonthly = 16;
  const proAnnualTotal = proAnnualMonthly * 12;

  const premiumMonthly = 39;
  const premiumAnnualMonthly = 33;
  const premiumAnnualTotal = premiumAnnualMonthly * 12;

  const executiveMonthly = 49;
  const executiveAnnualMonthly = 41;
  const executiveAnnualTotal = executiveAnnualMonthly * 12;

  const savingsPercent = Math.round(
    ((proMonthly - proAnnualMonthly) / proMonthly) * 100
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-white to-stone-100 py-16 px-4 flex flex-col items-center">
      <main className="w-full max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            ATS tools, resume builders, interview prep, and LinkedIn Premium cost $150+/month separately. We replaced them all.
          </p>
          <p className="inline-flex items-center gap-2 mt-5 bg-green-50 border border-green-200 text-green-800 text-sm font-semibold px-4 py-2 rounded-full">
            <ShieldCheck className="w-4 h-4 text-green-600" />
            Claim verification included on every plan — each resume is checked against your real career history.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <span
            className={`text-sm font-semibold ${
              !annual ? 'text-gray-900' : 'text-gray-400'
            }`}
          >
            Monthly
          </span>
          <button
            onClick={() => setAnnual(!annual)}
            className={`relative w-14 h-7 rounded-full transition-colors duration-200 ${
              annual ? 'bg-blue-600' : 'bg-gray-300'
            }`}
            aria-label="Toggle billing period"
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200 ${
                annual ? 'translate-x-7' : 'translate-x-0'
              }`}
            />
          </button>
          <span
            className={`text-sm font-semibold ${
              annual ? 'text-gray-900' : 'text-gray-400'
            }`}
          >
            Annual
          </span>
          {annual && (
            <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">
              Save {savingsPercent}%
            </span>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 max-w-6xl mx-auto mb-16">
          {/* Free Plan */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 flex flex-col">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Free</h2>
            <p className="text-gray-600 mb-6">
              Get started with your job search essentials.
            </p>
            <div className="mb-6">
              <span className="text-5xl font-extrabold text-gray-900">$0</span>
              <span className="text-gray-500 ml-2">/month</span>
            </div>
            <a
              href="https://app.superpowerresume.com/sign-up"
              className="w-full inline-block text-center bg-gray-900 hover:bg-gray-800 text-white font-bold py-3 px-6 rounded-xl transition-colors mb-8"
            >
              Get Started Free
            </a>
            <ul className="space-y-3 flex-1">
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                3 tailored resumes/month
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                1 cover letter/month
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                Basic ATS match scoring
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                1 AI career interview
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                1 voice mock interview/month
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                5 document uploads
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                10 AI chat messages/month
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                Email templates
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                Track up to 10 jobs
              </li>
            </ul>
          </div>

          {/* Pro Plan */}
          <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-500 p-8 flex flex-col relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-bold px-4 py-1 rounded-full shadow">
              Most Popular
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Pro</h2>
            <p className="text-gray-600 mb-6">
              For serious job seekers who want every advantage.
            </p>
            <div className="mb-6">
              <span className="text-5xl font-extrabold text-gray-900">
                ${annual ? proAnnualMonthly : proMonthly}
              </span>
              <span className="text-gray-500 ml-2">/month</span>
              {annual && (
                <div className="text-sm text-gray-500 mt-1">
                  ${proAnnualTotal}/year &middot; billed annually
                </div>
              )}
            </div>
            <a
              href="https://app.superpowerresume.com/sign-up"
              className="w-full inline-block text-center bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold py-3 px-6 rounded-xl shadow transition-colors mb-8"
            >
              Start Pro Plan
            </a>
            <ul className="space-y-3 flex-1">
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>25</b> tailored resumes/month</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>25</b> cover letters/month</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                Advanced ATS scoring + keyword gap analysis
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>25</b> document uploads</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>5</b> AI career interviews/month</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>5</b> AI mock interviews/month</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>100</b> AI chat messages/month</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                Email templates with AI personalization
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span>All PDF resume templates</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>Unlimited</b> job tracking</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                Priority support
              </li>
            </ul>
          </div>

          {/* Premium Plan */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 flex flex-col relative">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Premium</h2>
            <p className="text-gray-600 mb-6">
              Maximum power for your entire job search.
            </p>
            <div className="mb-6">
              <span className="text-5xl font-extrabold text-gray-900">
                ${annual ? premiumAnnualMonthly : premiumMonthly}
              </span>
              <span className="text-gray-500 ml-2">/month</span>
              {annual && (
                <div className="text-sm text-gray-500 mt-1">
                  ${premiumAnnualTotal}/year &middot; billed annually
                </div>
              )}
            </div>
            <a
              href="https://app.superpowerresume.com/sign-up"
              className="w-full inline-block text-center bg-gray-900 hover:bg-gray-800 text-white font-bold py-3 px-6 rounded-xl transition-colors mb-8"
            >
              Start Premium Plan
            </a>
            <ul className="space-y-3 flex-1">
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>100</b> tailored resumes/month</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>100</b> cover letters/month</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                Advanced ATS scoring + keyword gap analysis
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>100</b> document uploads</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>Unlimited</b> AI career interviews</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>20</b> AI mock interviews/month</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>500</b> AI chat messages/month</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                Email templates with AI personalization
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>20</b> AI headshot enhancements/month</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                LinkedIn optimization suite
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span>All PDF resume templates</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                <span><b>Unlimited</b> job tracking</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <Check className="w-5 h-5 text-green-600 shrink-0" />
                Priority support
              </li>
            </ul>
          </div>

          {/* Executive Plan */}
          <div className="bg-gray-900 rounded-2xl shadow-xl border border-gray-700 p-8 flex flex-col relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gray-700 to-gray-600 text-white text-sm font-bold px-4 py-1 rounded-full shadow border border-gray-500">
              For Senior Leaders
            </div>
            <h2 className="text-2xl font-extrabold text-white mb-2">Executive</h2>
            <p className="text-gray-300 mb-6">
              Executive positioning, not just a resume.
            </p>
            <div className="mb-6">
              <span className="text-5xl font-extrabold text-white">
                ${annual ? executiveAnnualMonthly : executiveMonthly}
              </span>
              <span className="text-gray-400 ml-2">/month</span>
              {annual && (
                <div className="text-sm text-gray-400 mt-1">
                  ${executiveAnnualTotal}/year &middot; billed annually
                </div>
              )}
            </div>
            <a
              href="https://app.superpowerresume.com/sign-up"
              className="w-full inline-block text-center bg-white hover:bg-gray-100 text-gray-900 font-bold py-3 px-6 rounded-xl shadow transition-colors mb-8"
            >
              Start Executive Plan
            </a>
            <ul className="space-y-3 flex-1">
              <li className="flex items-center gap-3 text-gray-200">
                <Check className="w-5 h-5 text-green-400 shrink-0" />
                <span>Everything in Premium, <b>unlimited</b></span>
              </li>
              <li className="flex items-center gap-3 text-gray-200">
                <Check className="w-5 h-5 text-green-400 shrink-0" />
                <span><b>Unlimited</b> resumes, cover letters &amp; documents</span>
              </li>
              <li className="flex items-center gap-3 text-gray-200">
                <Check className="w-5 h-5 text-green-400 shrink-0" />
                <span><b>Unlimited</b> voice mock interviews</span>
              </li>
              <li className="flex items-center gap-3 text-gray-200">
                <Check className="w-5 h-5 text-green-400 shrink-0" />
                <span><b>Executive Suite</b>:</span>
              </li>
              <li className="flex items-center gap-3 text-gray-200 pl-8">
                <Check className="w-4 h-4 text-green-400 shrink-0" />
                Value Profiles backed by proof
              </li>
              <li className="flex items-center gap-3 text-gray-200 pl-8">
                <Check className="w-4 h-4 text-green-400 shrink-0" />
                Executive-grade documents
              </li>
              <li className="flex items-center gap-3 text-gray-200 pl-8">
                <Check className="w-4 h-4 text-green-400 shrink-0" />
                Role playbooks
              </li>
              <li className="flex items-center gap-3 text-gray-200 pl-8">
                <Check className="w-4 h-4 text-green-400 shrink-0" />
                AI role research
              </li>
            </ul>
            <a
              href="/executive"
              className="mt-6 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors text-center"
            >
              Learn more about Executive &rarr;
            </a>
          </div>
        </div>

        {/* Feature Comparison Table */}
        <div className="rounded-2xl shadow-lg bg-white border border-gray-100 overflow-hidden mb-16">
          <div className="px-6 py-6 border-b border-gray-100">
            <h2 className="text-2xl font-extrabold text-gray-900 text-center">
              Full Feature Comparison
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left font-bold text-gray-800">
                    Feature
                  </th>
                  <th className="px-6 py-4 text-center font-bold text-gray-800 w-32">
                    Free
                  </th>
                  <th className="px-6 py-4 text-center font-bold text-blue-700 w-32">
                    Pro
                  </th>
                  <th className="px-6 py-4 text-center font-bold text-gray-800 w-32">
                    Premium
                  </th>
                  <th className="px-6 py-4 text-center font-bold text-gray-900 w-32">
                    Executive
                  </th>
                </tr>
              </thead>
              <tbody>
                {features.map(({ name, free, pro, premium, executive }, idx) => (
                  <tr
                    key={name}
                    className={
                      idx % 2 === 0
                        ? 'bg-white hover:bg-blue-50/40'
                        : 'bg-gray-50/50 hover:bg-blue-50/40'
                    }
                  >
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {name}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <FeatureValue value={free} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <FeatureValue value={pro} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <FeatureValue value={premium} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <FeatureValue value={executive} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ / Trust */}
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-extrabold text-gray-900 text-center mb-8">
            Common Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-bold text-gray-900 mb-1">
                Do I need a credit card to start?
              </h3>
              <p className="text-gray-600">
                No. The Free plan requires no credit card. Just sign up and start
                creating resumes immediately.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">
                Can I cancel anytime?
              </h3>
              <p className="text-gray-600">
                Yes. Cancel your subscription at any time. You&apos;ll keep
                access through the end of your billing period.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">
                What&apos;s the difference between Pro and Premium?
              </h3>
              <p className="text-gray-600">
                Pro gives you generous limits for an active job search — 5 voice
                mock interviews a month (Free includes 1), all PDF templates,
                LinkedIn optimization, and AI-personalized email templates.
                Premium gives you maximum capacity — up to 100 resumes/month, 20
                voice interviews, and 500 AI chat messages — ideal for career
                changers and power users applying to many roles.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">
                What is the Executive plan?
              </h3>
              <p className="text-gray-600">
                Executive is built for senior leaders. It includes everything in
                Premium with unlimited usage, plus the Executive Suite: Value
                Profiles backed by proof from your real record, executive-grade
                documents, role playbooks, and AI role research.{' '}
                <a href="/executive" className="text-blue-600 hover:text-blue-800 font-semibold">
                  Learn more about Executive &rarr;
                </a>
              </p>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">
                Is claim verification really on every plan?
              </h3>
              <p className="text-gray-600">
                Yes. Every plan — including Free — verifies each resume claim
                against your real career history and shows you exactly where
                every bullet came from. Trust isn&apos;t a paid add-on.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">
                What happens when I hit my monthly limit?
              </h3>
              <p className="text-gray-600">
                You can still access your existing resumes and documents. Your
                limits reset at the start of each billing cycle, or you can
                upgrade for more.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-1">
                Is there a refund policy?
              </h3>
              <p className="text-gray-600">
                Yes. If you&apos;re not satisfied, contact us within 7 days of
                your charge for a full refund.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <section className="flex justify-center">
          <div className="relative bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl shadow-2xl px-8 py-12 max-w-xl w-full flex flex-col items-center">
            <h2 className="text-3xl font-extrabold mb-4 text-center text-white tracking-tight">
              Ready to land more interviews?
            </h2>
            <p className="text-lg text-blue-100 mb-8 text-center">
              Start with the Free plan — no credit card required.
            </p>
            <a
              href="https://app.superpowerresume.com/sign-up"
              className="inline-block bg-white text-blue-700 font-bold py-3 px-8 rounded-xl shadow text-lg border border-transparent text-center hover:bg-blue-50 transition"
            >
              Get Started Free
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
